import React, { useEffect, useState } from 'react';
import { ReactTags } from 'react-tag-autocomplete';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import { PROFILE_STATUS_OPTIONS } from '../../../config/enum-options.js';
import { INTERNAL_MESSAGE_TYPE, TARGET_TYPE } from '../model/internal-mail.model.js';
import { useComplianceMode } from '../../system-navigation/context/compliance-mode-context.jsx';

const PROFILE_STATUS_SUGGESTIONS = PROFILE_STATUS_OPTIONS.map((s) => ({
  value: s,
  label: s.charAt(0).toUpperCase() + s.slice(1),
}));

function toTags(values) {
  return (values || []).map((v) => ({ value: v, label: v }));
}

function TagsSelect({ selected = [], suggestions, onChange, placeholder }) {
  const tags = toTags(selected);

  const handleDelete = (index) => {
    onChange(selected.filter((_, i) => i !== index));
  };

  const handleAdd = (tag) => {
    const val = String(tag?.value || '').trim();
    if (!val || selected.includes(val)) return;
    onChange([...selected, val]);
  };

  return (
    <ReactTags
      selected={tags}
      suggestions={suggestions}
      allowNew={false}
      minQueryLength={0}
      onDelete={handleDelete}
      onAdd={handleAdd}
      placeholderText={placeholder}
    />
  );
}

function normalizeCountryOptions(payload) {
  const list = Array.isArray(payload?.countries) ? payload.countries : [];
  return list.map((item) => {
    const code = String(item?.code || '').toUpperCase();
    const name = String(item?.name || code);
    return code ? { value: code, label: `${code} — ${name}` } : null;
  }).filter(Boolean);
}

function InternalMailCreateModal({
  form,
  formErrors,
  isSubmitting,
  submitError,
  onFormChange,
  onSubmit,
  onClose,
  hideTargeting = false,
}) {
  const { token } = useAuth();
  const { t } = useTranslation();
  const { isFinancialRegulation } = useComplianceMode();
  const [countryOptions, setCountryOptions] = useState([]);

  const ACCOUNT_TYPE_OPTIONS = [
    { value: 'demo', label: t('internalMail.accountTypeDemo') },
    { value: 'live', label: t('internalMail.accountTypeLive') },
    { value: 'wallet', label: t('internalMail.accountTypeWallet') },
  ];

  useEffect(() => {
    let cancelled = false;
    adminApi.getCountryDictionary(token).then((res) => {
      if (!cancelled) setCountryOptions(normalizeCountryOptions(res));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    if (!isFinancialRegulation && form.type === INTERNAL_MESSAGE_TYPE.marketing) {
      onFormChange('type', INTERNAL_MESSAGE_TYPE.transactional);
    }
  }, [form.type, isFinancialRegulation, onFormChange]);

  return (
    <>
      <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{t('internalMail.modalTitle')}</h5>
              <button type="button" className="btn-close" onClick={onClose} disabled={isSubmitting}></button>
            </div>
            <form onSubmit={onSubmit}>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">{t('internalMail.labelSubject')}</label>
                  <input
                    type="text"
                    className={`form-control ${formErrors.subject ? 'is-invalid' : ''}`}
                    value={form.subject}
                    onChange={(event) => onFormChange('subject', event.target.value)}
                    maxLength={255}
                    disabled={isSubmitting}
                  />
                  {formErrors.subject ? <div className="invalid-feedback">{formErrors.subject}</div> : null}
                </div>

                <div className="mb-3">
                  <label className="form-label">{t('internalMail.labelBody')}</label>
                  <textarea
                    className={`form-control ${formErrors.body ? 'is-invalid' : ''}`}
                    value={form.body}
                    onChange={(event) => onFormChange('body', event.target.value)}
                    rows={5}
                    disabled={isSubmitting}
                  />
                  {formErrors.body ? <div className="invalid-feedback">{formErrors.body}</div> : null}
                </div>

                <div className="mb-3">
                  <label className="form-label">{t('internalMail.labelMessageType')}</label>
                  <select
                    className={`form-select ${formErrors.type ? 'is-invalid' : ''}`}
                    value={form.type}
                    onChange={(event) => onFormChange('type', event.target.value)}
                    disabled={isSubmitting}
                  >
                    <option value={INTERNAL_MESSAGE_TYPE.transactional}>{t('internalMail.optionTransactional')}</option>
                    {isFinancialRegulation ? (
                      <option value={INTERNAL_MESSAGE_TYPE.marketing}>{t('internalMail.optionMarketing')}</option>
                    ) : null}
                  </select>
                  {formErrors.type ? <div className="invalid-feedback">{formErrors.type}</div> : null}
                  {isFinancialRegulation && form.type === INTERNAL_MESSAGE_TYPE.marketing ? (
                    <div className="form-text">{t('internalMail.marketingConsentHint')}</div>
                  ) : null}
                </div>

                {!hideTargeting ? (
                  <>
                    <div className="mb-3">
                      <label className="form-label">{t('internalMail.labelTargeting')}</label>
                      <select
                        className="form-select"
                        value={form.targetType}
                        onChange={(event) => onFormChange('targetType', event.target.value)}
                        disabled={isSubmitting}
                      >
                        <option value={TARGET_TYPE.all}>{t('internalMail.optionAllClients')}</option>
                        <option value={TARGET_TYPE.filter}>{t('internalMail.optionFilterByCriteria')}</option>
                      </select>
                    </div>

                    {form.targetType === TARGET_TYPE.filter ? (
                      <div className="row g-3">
                        <div className="col-12">
                          <label className="form-label">{t('internalMail.labelCountries')}</label>
                          <TagsSelect
                            selected={form.targetCountries}
                            suggestions={countryOptions}
                            onChange={(val) => onFormChange('targetCountries', val)}
                            placeholder={t('internalMail.placeholderCountries')}
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">{t('internalMail.labelAccountTypes')}</label>
                          <TagsSelect
                            selected={form.targetAccountTypes}
                            suggestions={ACCOUNT_TYPE_OPTIONS}
                            onChange={(val) => onFormChange('targetAccountTypes', val)}
                            placeholder={t('internalMail.placeholderAccountTypes')}
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">{t('internalMail.labelProfileStatuses')}</label>
                          <TagsSelect
                            selected={form.targetProfileStatuses}
                            suggestions={PROFILE_STATUS_SUGGESTIONS}
                            onChange={(val) => onFormChange('targetProfileStatuses', val)}
                            placeholder={t('internalMail.placeholderStatuses')}
                          />
                        </div>
                      </div>
                    ) : null}
                  </>
                ) : null}

                {submitError ? (
                  <div className="alert alert-danger mt-3 mb-0">{submitError}</div>
                ) : null}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isSubmitting}>
                  {t('internalMail.cancelButton')}
                </button>
                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? t('internalMail.sendingButton') : t('internalMail.sendButton')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
}

export default InternalMailCreateModal;
